import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

import type { PendingBankExpense } from '@/lib/api/expense-sync-api'
import type { ExpenseSyncSearch } from '@/lib/schemas/expense-sync-search'
import { ExpenseSyncConfirmDialog } from '@/components/expenses/expense-sync-confirm-dialog'
import { Button } from '@/components/ui/button'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import { useExpenseCategories } from '@/hooks/use-expense-category-query'
import {
    useConfirmExpenseSync,
    useIgnoreExpenseSync,
    usePendingBankExpenses,
} from '@/hooks/use-expense-sync-query'
import { formatVnd } from '@/lib/format'
import { m } from '@/lib/paraglide/messages'
import { normalizeExpenseSyncSearch } from '@/lib/schemas/expense-sync-search'

const PER_PAGE = 20

export const Route = createFileRoute(
    '/_app/_authed/_sidebar/accounting/expense-sync',
)({
    component: ExpenseSyncPage,
    // Trang đẩy lên search param → reload / share link / back giữ nguyên
    // trạng thái đang xem.
    validateSearch: normalizeExpenseSyncSearch,
})

function ExpenseSyncPage() {
    const navigate = useNavigate({ from: '/accounting/expense-sync' })
    const urlSearch = Route.useSearch()
    const setSearch = (
        updater: (prev: ExpenseSyncSearch) => ExpenseSyncSearch,
        opts?: { replace?: boolean },
    ) => void navigate({ search: updater, replace: opts?.replace ?? false })

    const page = urlSearch.page ?? 1
    const [confirming, setConfirming] = useState<PendingBankExpense | null>(
        null,
    )

    const { data: catRes } = useExpenseCategories({
        is_active: true,
        per_page: 100,
    })
    const categories = catRes?.data ?? []

    const { data: listRes } = usePendingBankExpenses({
        page,
        per_page: PER_PAGE,
    })
    const rows = listRes?.data ?? []
    const meta = listRes?.meta
    const lastPage = Math.max(1, meta?.total_pages ?? 1)

    // Duyệt/bỏ qua giao dịch cuối của trang cuối làm trang hiện tại vượt quá
    // tổng số trang — kéo về trang cuối còn dữ liệu thay vì hiện bảng rỗng.
    useEffect(() => {
        if (!meta || page <= lastPage) return
        setSearch(
            (prev) => ({
                ...prev,
                page: lastPage > 1 ? lastPage : undefined,
            }),
            { replace: true },
        )
    }, [meta, page, lastPage])

    const confirmMut = useConfirmExpenseSync()
    const ignoreMut = useIgnoreExpenseSync()

    return (
        <div className="space-y-4 p-4">
            <h1 className="text-xl font-semibold">{m.expense_sync_title()}</h1>

            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead>{m.expense_sync_transacted_at()}</TableHead>
                        <TableHead>{m.expense_sync_content()}</TableHead>
                        <TableHead className="text-right">
                            {m.expense_amount()}
                        </TableHead>
                        <TableHead>{m.expense_sync_reference()}</TableHead>
                        <TableHead />
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {rows.length === 0 && (
                        <TableRow>
                            <TableCell
                                colSpan={5}
                                className="text-center text-muted-foreground py-8"
                            >
                                {m.expense_sync_empty()}
                            </TableCell>
                        </TableRow>
                    )}
                    {rows.map((row) => (
                        <TableRow key={row.id}>
                            <TableCell>
                                {new Date(row.transacted_at).toLocaleString(
                                    'vi-VN',
                                )}
                            </TableCell>
                            <TableCell className="max-w-[24rem] truncate">
                                {row.content}
                            </TableCell>
                            <TableCell className="text-right font-mono">
                                {formatVnd(row.amount)}
                            </TableCell>
                            <TableCell className="font-mono text-xs">
                                {row.reference_code ?? '—'}
                            </TableCell>
                            <TableCell className="text-right space-x-2">
                                <Button
                                    size="sm"
                                    onClick={() => setConfirming(row)}
                                >
                                    {m.expense_sync_confirm()}
                                </Button>
                                <Button
                                    size="sm"
                                    variant="outline"
                                    disabled={ignoreMut.isPending}
                                    onClick={() => {
                                        if (
                                            window.confirm(
                                                m.expense_sync_ignore_confirm(),
                                            )
                                        ) {
                                            ignoreMut.mutate({
                                                id: row.id,
                                                data: {},
                                            })
                                        }
                                    }}
                                >
                                    {m.expense_sync_ignore()}
                                </Button>
                            </TableCell>
                        </TableRow>
                    ))}
                </TableBody>
            </Table>

            {meta && meta.total > 0 && (
                <div className="flex items-center justify-between text-sm text-muted-foreground">
                    <span>
                        {m.expense_sync_total_count({
                            count: String(meta.total),
                        })}
                    </span>
                    {meta.total_pages > 1 && (
                        <div className="flex gap-2">
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={page === 1}
                                onClick={() =>
                                    setSearch((prev) => ({
                                        ...prev,
                                        page:
                                            page - 1 > 1 ? page - 1 : undefined,
                                    }))
                                }
                            >
                                {m.common_pagination_prev()}
                            </Button>
                            <span className="flex items-center px-2">
                                {page} / {meta.total_pages}
                            </span>
                            <Button
                                size="sm"
                                variant="outline"
                                disabled={page >= meta.total_pages}
                                onClick={() =>
                                    setSearch((prev) => ({
                                        ...prev,
                                        page: page + 1,
                                    }))
                                }
                            >
                                {m.common_pagination_next()}
                            </Button>
                        </div>
                    )}
                </div>
            )}

            {confirming && (
                <ExpenseSyncConfirmDialog
                    event={confirming}
                    categories={categories}
                    open
                    onOpenChange={(open) => {
                        if (!open) setConfirming(null)
                    }}
                    onConfirm={(values) => {
                        confirmMut.mutate(
                            { id: confirming.id, data: values },
                            { onSuccess: () => setConfirming(null) },
                        )
                    }}
                    submitting={confirmMut.isPending}
                />
            )}
        </div>
    )
}
